import { Modal, Button, Checkbox, Tag, Input } from 'antd'
import { Fragment, SetStateAction, useState } from 'react'
import { Formik, Form, FormikState, FormikHelpers } from 'formik'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import groupBy from 'lodash.groupby'
import sortBy from 'lodash.sortby'
import { produce } from 'immer'
import { Info } from 'react-feather'

import {
  FileUpload,
  useFeedbackOptions,
  useShowMessage,
  useAuthState,
  Tooltip,
} from '@pepper/ui'

import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'
import { updateAssignmentStatus } from '@pepper/utils/src/api/assignments/assignments'
import { FEEDBACK_TOOLTIPS } from '@pepper/utils/src/constant/enum/feedback_tooltips_info'
import getValidationSchema from './getValidationSchema'

import styles from './BulkReworkModal.module.css'

interface BulkReworkModalProps {
  assignments: Assignment[] | Item[]
  onCancel?: () => void
  refetch?: () => void
}

type Values = Record<
  string,
  {
    comment: string
    attachments: any[]
    [key: number]: number[]
  }
>
const GET_FLAGGING_OPTION = true

const BulkReworkModal = NiceModal.create(
  ({ assignments, onCancel, refetch }: BulkReworkModalProps): JSX.Element => {
    const modal = useModal()
    const showMessage = useShowMessage()
    const { user } = useAuthState()

    const sorted = sortBy(assignments, 'category.id')
    const group = groupBy(sorted, 'category.key')
    const groupLength = Object.keys(group).length - 1

    const [step, setStep] = useState(0)

    const reworkAreas = {
      text: useFeedbackOptions({
        type: 'text',
        getFlaggingOption: GET_FLAGGING_OPTION,
      }),
      translation: useFeedbackOptions({
        type: 'translation',
        getFlaggingOption: GET_FLAGGING_OPTION,
      }),
      design: useFeedbackOptions({
        type: 'design',
        getFlaggingOption: GET_FLAGGING_OPTION,
      }),
      others: useFeedbackOptions({
        type: 'others',
        getFlaggingOption: GET_FLAGGING_OPTION,
      }),
    }

    const handleOnClose = (
      resetForm: (nextState?: Partial<FormikState<Values>>) => void
    ) => {
      setStep(0)
      onCancel?.()
      resetForm()
      modal.remove()
    }

    const handleOnNextButtonClick = (): void => {
      setStep(step + 1)
    }

    const handleOnPreviousButtonClick = (): void => {
      setStep(step - 1)
    }

    const handleOnSubmit = async (
      values: Values,
      formikHelpers: FormikHelpers<Values>
    ) => {
      try {
        if (!user) return
        const result = await Promise.allSettled(
          assignments.map(_assignment => {
            const { key } = _assignment.category
            const { comment, attachments, ...rest } = values[key]
            const body = {
              assigner_role: 'client',
              assignee_id: user.uid,
              assigner_id: user.uid,
              reason: comment || '',
              rework_attachments: attachments.map(attachment => attachment.id),
              rework_areas: reworkAreas[key]
                .filter(reworkArea => {
                  return Array.isArray(rest[reworkArea.id])
                })
                .map(reworkArea => reworkArea.key),
              rework_areas_data: Object.keys(rest).map(_key => {
                return {
                  id: +_key,
                  inputs: rest[+_key],
                }
              }),
              status: ASSIGNMENT_STATUS.CLI_RWK.id,
            }
            return updateAssignmentStatus({ id: _assignment.id, body })
          })
        )
        const rejected = result.filter(_result => _result.status === 'rejected')
        if (rejected.length > 0) {
          showMessage({
            type: 'error',
            message: `Failed to rework ${rejected.length} assignments.`,
          })
          return
        }
        showMessage({
          type: 'success',
          message: `${result.length} Assignments marked rework successfully.`,
        })
        handleOnClose(formikHelpers.resetForm)
        refetch?.()
      } catch (error) {
        if (error instanceof Error) {
          showMessage({ type: 'error', message: error.message })
          return
        }
        showMessage({ type: 'error', message: 'Something went wrong!!' })
      }
    }

    const handleOnReworkAreaChange = ({
      values,
      key,
      reworkAreaId,
      setValues,
    }: {
      values: Values
      key: string
      reworkAreaId: number
      setValues: (
        values: SetStateAction<Values>,
        shouldValidate?: boolean | undefined
      ) => void
    }) => {
      const nextValues = produce(values, draft => {
        if (draft[key]) {
          if (!draft[key][reworkAreaId]) {
            draft[key][reworkAreaId] = []
          } else {
            delete draft[key][reworkAreaId]
          }
        }
      })

      setValues(nextValues)
    }

    const handleOnReworkInputChange = ({
      values,
      key,
      reworkInputId,
      reworkAreaId,
      setValues,
    }: {
      values: Values
      key: string
      reworkInputId: number
      reworkAreaId: number
      setValues: (
        values: SetStateAction<Values>,
        shouldValidate?: boolean | undefined
      ) => void
    }) => {
      const nextValues = produce(values, draft => {
        if (draft[key] && reworkAreaId !== 25) {
          if (!draft[key][reworkAreaId].includes(reworkInputId)) {
            draft[key][reworkAreaId].push(reworkInputId)
          } else {
            draft[key][reworkAreaId] = draft[key][reworkAreaId].filter(
              input => input !== reworkInputId
            )
          }
        } else if (draft[key] && reworkAreaId === 25) {
          if (!draft[key][reworkAreaId].includes(reworkInputId)) {
            draft[key][reworkAreaId] = [reworkInputId]
          } else {
            draft[key][reworkAreaId] = draft[key][reworkAreaId].filter(
              input => input !== reworkInputId
            )
          }
        }
      })

      setValues(nextValues)
    }

    const handleOnCommentChange = ({
      values,
      newValue,
      key,
      setValues,
    }: {
      values: Values
      newValue: string
      key: string
      setValues: (
        values: SetStateAction<Values>,
        shouldValidate?: boolean
      ) => void
    }) => {
      const nextValues = produce(values, draft => {
        if (draft[key]) {
          if (typeof draft[key].comment === 'string') {
            draft[key].comment = newValue
          } else {
            draft[key].comment = ''
          }
        }
      })

      setValues(nextValues)
    }

    const handleOnAttachmentUpload = ({
      values,
      newFile,
      key,
      setValues,
    }: {
      values: Values
      newFile: any[]
      key: string
      setValues: (
        values: SetStateAction<Values>,
        shouldValidate?: boolean
      ) => void
    }) => {
      const nextValues = produce(values, draft => {
        if (draft[key]) {
          if (typeof draft[key].comment === 'string') {
            draft[key].attachments = newFile
          } else {
            draft[key].attachments = []
          }
        }
      })

      setValues(nextValues)
    }

    const initialValues: Values = {}

    Object.keys(reworkAreas)
      .filter(key => group[key])
      .forEach(key => {
        initialValues[key] = { comment: '', attachments: [] }
      })

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleOnSubmit}
        validationSchema={getValidationSchema({
          reworkAreas,
          categories: Object.keys(group),
        })}
        validateOnBlur={false}
      >
        {({ submitForm, resetForm, values, errors, setValues, isValid }) => {
          return (
            <Form>
              <Modal
                visible={modal.visible}
                width={725}
                centered={true}
                className={styles.modal}
                onCancel={() => handleOnClose(resetForm)}
                footer={[
                  <div className={styles.footer} key="1">
                    <span>
                      {step + 1} of {groupLength + 1}
                    </span>
                    <div>
                      <Button
                        type="link"
                        onClick={
                          step > 0
                            ? handleOnPreviousButtonClick
                            : () => handleOnClose(resetForm)
                        }
                      >
                        {step > 0 ? 'Back' : 'Cancel'}
                      </Button>
                      <Button
                        type="primary"
                        onClick={
                          step === groupLength
                            ? submitForm
                            : handleOnNextButtonClick
                        }
                        disabled={step === groupLength && !isValid}
                      >
                        {step === groupLength ? 'Request Rework' : 'Next'}
                      </Button>
                    </div>
                  </div>,
                ]}
                title={`Select issues with the ${Object.keys(group).join(
                  ', '
                )} related assignments`}
              >
                {Object.keys(group).map((key, idx) => {
                  if (step !== idx) return null
                  if (!values[key]) return null

                  return (
                    <Fragment key={key}>
                      <span className={styles.note}>
                        {group[key].length} out of {assignments.length}{' '}
                        assignments selected are {key} related assignments.
                        Please select issues you want us to resolve on {key}{' '}
                        related assignments.
                      </span>
                      {typeof errors[key] === 'string' && (
                        <div className={styles.error}>{errors[key]}</div>
                      )}
                      {reworkAreas[key as ProjectCategory].map(reworkArea => {
                        const checked = Array.isArray(
                          values[key][reworkArea.id]
                        )
                        const isErrorShown =
                          typeof errors[key] !== 'string' &&
                          errors[key]?.[reworkArea.id]

                        return (
                          reworkArea.key !== 'flag_creator' && (
                            <div key={reworkArea.id} className={styles.field}>
                              <Checkbox
                                checked={checked}
                                onChange={() =>
                                  handleOnReworkAreaChange({
                                    values,
                                    key,
                                    reworkAreaId: reworkArea.id,
                                    setValues,
                                  })
                                }
                                className={styles.flagCheckBox}
                              >
                                <span className={styles.issueTitle}>
                                  {reworkArea.title}
                                  <Tooltip
                                    placement="bottom"
                                    overlayClassName={styles.tooltip}
                                    title={
                                      FEEDBACK_TOOLTIPS[reworkArea.key]
                                        ?.reworkTooltipText ?? reworkArea.title
                                    }
                                  >
                                    <Info
                                      size={16}
                                      className={styles.infoIcon}
                                    />
                                  </Tooltip>
                                </span>
                              </Checkbox>

                              {checked && (
                                <div className={styles.tags}>
                                  {reworkArea.rework_inputs.map(reworkInput => {
                                    return (
                                      <Tag.CheckableTag
                                        checked={values[key][
                                          reworkArea.id
                                        ].includes(reworkInput.id)}
                                        onChange={() =>
                                          handleOnReworkInputChange({
                                            values,
                                            key,
                                            reworkInputId: reworkInput.id,
                                            reworkAreaId: reworkArea.id,
                                            setValues,
                                          })
                                        }
                                        key={reworkInput.id}
                                      >
                                        {reworkInput.title}
                                      </Tag.CheckableTag>
                                    )
                                  })}
                                </div>
                              )}
                              {isErrorShown && (
                                <div className={styles.error}>
                                  {errors[key]?.[reworkArea.id]}
                                </div>
                              )}
                            </div>
                          )
                        )
                      })}
                      <div className={styles.field}>
                        <label className={styles.label} htmlFor="comment">
                          Add your comments *
                        </label>
                        <Input.TextArea
                          id="comment"
                          className={styles.textarea}
                          value={values[key as ProjectCategory].comment}
                          onChange={event =>
                            handleOnCommentChange({
                              values,
                              key,
                              newValue: event.target.value,
                              setValues,
                            })
                          }
                        />
                        <FileUpload
                          className={styles.upload}
                          multipleSelection={true}
                          multipleUpload={true}
                          defaultFileList={values[key].attachments}
                          handleFiles={_files =>
                            handleOnAttachmentUpload({
                              values,
                              key,
                              newFile: Array.isArray(_files)
                                ? _files
                                : [_files],
                              setValues,
                            })
                          }
                        />
                      </div>
                    </Fragment>
                  )
                })}
              </Modal>
            </Form>
          )
        }}
      </Formik>
    )
  }
)

export default BulkReworkModal
